import React from 'react';
import Image from 'next/legacy/image';
import { getMerchantName } from '../../helpers/flyerItems';
import { FlyerDetailsViewData, FlyerItemViewData, MerchantViewData } from '../../helpers/flyers';
import { getFlyerItemUrl } from '../../helpers/links';
import { useRouter } from 'next/router';
import { Locale } from '../../helpers/common';
import { convertTimestampToDate, convertTimestampToISODate } from '../../helpers/date';

interface FlyerItemProps {
  item?: FlyerItemViewData;
  merchant?: MerchantViewData;
  flyer?: FlyerDetailsViewData;
  location: string;
}

export default function FlyerItem({ item, merchant, flyer, location }: FlyerItemProps): JSX.Element {
  const router = useRouter();
  const locale = router.locale as Locale;

  function imgLoader({ src }) {
    return src;
  }

  const getStructuredDataBlock = () => {
    const url = getFlyerItemUrl(item.id, item.name, location, locale);
    const { brand, name, description, thumbnail_url } = item;
    const currentPrice = Number(item.current_price).toFixed(2);
    const itemName = normalizeName(name);
    const brandName = normalizeName(brand);
    return (
      <div>
        <div className="structure-data">
          <div itemType="http://schema.org/Product" itemScope>
            <meta itemProp="name" content={itemName} />
            {thumbnail_url ? <link itemProp="image" href={thumbnail_url} /> : null}
            <meta itemProp="description" content={description ? description : name} />
            <div itemProp="offers" itemType="http://schema.org/Offer" itemScope>
              <link itemProp="url" href={url} />
              <meta itemProp="availability" content="https://schema.org/InStock" />
              {currentPrice ? <meta itemProp="price" content={currentPrice} /> : null}
              <meta itemProp="priceCurrency" content="USD" />
              <meta itemProp="priceValidUntil" content={getValidityTime('valid_to')} />
            </div>
            {brandName ? (
              <div itemProp="brand" itemType="https://schema.org/Brand" itemScope>
                <meta itemProp="name" content={brandName} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const normalizeName = (name: string): string => {
    return name.replace(/(\r\n|\n|\r)/gm, '').slice(0, 64);
  };

  const getOriginalPriceBlock = () => {
    if (item.original_price) {
      const youSaveBlock = locale === 'fr-ca' ? 'Vous économisez' : 'You save';
      const discountPrice = Number(item.original_price) - Number(item.current_price);
      const percent = Math.floor(
        ((Number(item.original_price) - Number(item.current_price)) / Number(item.original_price)) * 100,
      );
      const percentBlock = locale === 'fr-ca' ? `Rabais de ${percent}%` : `${percent}% off`;
      return (
        <span>
          <div className="fade">
            <div className="original-price-info">
              {getPriceValueBlock(locale, item.original_price)}
              <span>{youSaveBlock}</span>
              {getPriceValueBlock(locale, discountPrice)}
              <span>{percentBlock}</span>
            </div>
          </div>
        </span>
      );
    }

    return '';
  };

  const getPriceBlock = () => {
    const showPrice = item.pre_price_text || item.current_price || item.price_text;
    const prePriceText = item.pre_price_text ? <span className="pre-price-text">{item.pre_price_text}</span> : '';
    const priceText = item.price_text ? <span className="price-text">{item.price_text}</span> : '';
    if (showPrice) {
      return (
        <span>
          <div>
            <div className="item-price-info">
              {prePriceText}
              {getPriceValueBlock(locale, item.current_price)}
              {priceText}
            </div>
          </div>
        </span>
      );
    }

    return '';
  };

  const getPriceValueBlock = (locale: string, currentPrice) => {
    const [dollars, cents] = getPriceValue(currentPrice);
    return (
      <span>
        <span className="dollars">{dollars}</span>.<span className="cents">{cents}</span>$
      </span>
    );
  };

  const getImageBlock = () => {
    return (
      <div className="item-info-image">
        <Image loader={imgLoader} width={100} height={100} alt={item.name} src={item.thumbnail_url} />
      </div>
    );
  };

  const getMerchantImageBlock = () => {
    if (merchant && merchant.thumbnail_url) {
      const url = merchant.thumbnail_url;
      const name = getMerchantName(locale, merchant);
      return <Image loader={imgLoader} width={100} height={100} className="logo" src={url} alt={name} />;
    }

    return '';
  };

  const getSku = () => {
    if (item.sku) {
      return <div className="flyer-item-sku">SKU: {item.sku}</div>;
    }
    return '';
  };

  const getDescription = () => {
    if (item.description) {
      return (
        <div>
          <h3>Description</h3>
          <span>{item.description}</span>
        </div>
      );
    }
    return '';
  };

  const getSalesStory = () => {
    return item.sale_story ? <div className="sale-story">{item.sale_story}</div> : '';
  };

  const getValidityTime = (field: string) => {
    if (item[field]) {
      return convertTimestampToISODate(item[field]);
    } else if (flyer && flyer[field]) {
      return convertTimestampToISODate(flyer[field]);
    }
  };

  const parseValidityTime = (field: string) => {
    const time = item[field];
    return time ? convertTimestampToDate(time) : '';
  };

  const getValidTo = () => {
    return parseValidityTime('valid_to');
  };

  const getValidFrom = () => {
    return parseValidityTime('valid_from');
  };

  const getPriceValue = (currentPrice: string) => {
    if (!currentPrice) {
      return [];
    }
    let price = currentPrice.toString();
    const matchWithoutCents = price.match(/^\s*([-&])?\s*(\$)?\s*(\d+)\s*$/);
    if (matchWithoutCents) {
      price += '.00';
    }

    const match = price.match(/^\s*([-&])?\s*(\$)?\s*(\d+)\.(\d+)\s*$/);
    if (!match) return;

    const dollars = match[3];

    let cents = match[4];
    if (!cents) cents = '00';
    else if (cents.length === 1) cents += '0';
    else if (cents.length > 2) cents = cents.substr(0, 2);

    return [dollars, cents];
  };

  if (!item) return null;

  return (
    <div>
      {getImageBlock()}
      {getMerchantImageBlock()}
      <h2>
        <span>{item.name}</span>
      </h2>
      <h1>
        {getPriceBlock()}
        {getOriginalPriceBlock()}
      </h1>
      <div>
        <div>
          <div>
            <div>
              <div>
                <section>
                  {getSalesStory()}
                  <div>
                    Valid {getValidFrom()} – {getValidTo()}
                  </div>
                </section>
                <section>
                  {getDescription()}
                  {getSku()}
                </section>
              </div>
            </div>
          </div>
        </div>
        {getStructuredDataBlock()}
      </div>
    </div>
  );
}
